<!--
  ActionButton.vue
  ================
  Composant bouton d'action réutilisable avec variantes.
  
  Fonctionnalités :
  - Variantes : primary, secondary, danger, success, warning, info
  - États : normal, loading, disabled
  - Tailles : sm, md, lg
  - Support des icônes et labels
  - Événements click personnalisables
  
  Composant UI de base utilisé dans toute l'application.
-->
<template>
  <button 
    :type="type"
    :disabled="disabled"
    :class="[
      'px-4 py-2 text-sm rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium min-h-[44px]',
      // Variants
      variant === 'primary' && 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
      variant === 'secondary' && 'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
      variant === 'success' && 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
      variant === 'danger' && 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
      variant === 'warning' && 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2',
      variant === 'info' && 'bg-cyan-600 text-white hover:bg-cyan-600 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2',
      variant === 'light' && 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
      variant === 'dark' && 'bg-gray-800 text-white hover:bg-gray-900 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
      // Action variants spécifiques
      variant === 'modifier' && 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 border border-blue-400',
      variant === 'upload' && 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 border border-blue-500',
      variant === 'upload-photo' && 'bg-cyan-500 text-white hover:bg-cyan-600 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 border border-cyan-400',
      // Sizes
      size === 'sm' && 'px-3 py-1.5 text-xs min-h-[36px]',
      size === 'md' && 'px-4 py-2 text-sm min-h-[40px]',
      size === 'lg' && 'px-6 py-3 text-base min-h-[44px]',
      size === 'xl' && 'px-8 py-4 text-lg min-h-[48px]',
      // States
      disabled && 'opacity-50 cursor-not-allowed',
      loading && 'cursor-wait',
      // Animations
      'hover:transform hover:scale-105 active:scale-95',
      // Custom classes
      customClass
    ]"
    @click="$emit('click', $event)"
  >
    <!-- Loading spinner -->
    <i v-if="loading" class="fas fa-spinner fa-spin"></i>
    
    <!-- Icon -->
    <template v-else-if="showIcon">
      <i v-if="icon" :class="icon"></i>
      <!-- Default icons for action variants -->
      <i v-else-if="variant === 'modifier'" class="fas fa-edit"></i>
      <i v-else-if="variant === 'upload'" class="fas fa-upload"></i>
      <i v-else-if="variant === 'upload-photo'" class="fas fa-camera"></i>
    </template>
    
    <!-- Label -->
    <span v-if="label" class="whitespace-nowrap">{{ label }}</span>
    
    <!-- Slot content -->
    <slot />
  </button>
</template>

<script setup lang="ts">
interface Props {
  label?: string;
  icon?: string;
  showIcon?: boolean;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'modifier' | 'upload' | 'upload-photo';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  customClass?: string;
}

// Props réactives
withDefaults(defineProps<Props>(), {
  showIcon: true,
  variant: 'primary',
  size: 'md',
  type: 'button',
  disabled: false,
  loading: false,
  customClass: ''
});

defineEmits<{
  click: [event: MouseEvent];
}>();
</script>

<style scoped>
</style> 